import styled, { css } from 'styled-components';
import { sectionWidth } from 'styles/spacings';
import { Radio } from 'components/RadioGroup';
import { ApButtonLink, colors } from '@alixpartners/ui-components';

import {
	black,
	grey200,
	grey500,
	colorError,
	hoverGreen,
	CHART_COLORS,
} from 'styles/colors';

export const slimRadioStyles = {
	padding: 6,
	paddingLeft: 0,
	minHeight: 30,
};

export const RightAlignFlex = styled.div`
	align-self: flex-end;
`;

export const SectionWrapper = styled.div`
	max-width: ${sectionWidth};
	min-width: ${sectionWidth};
	display: flex;
	width: 100%;
`;

export const IntroText = styled.div`
	font-size: 16px;
	letter-spacing: 0.3px;
	color: ${black};
	display: flex;
	align-items: center;

	span {
		margin-left: 8px;
	}
`;

export const InputWrapper = styled.div`
	display: inline-flex;
	flex-direction: row;
	align-items: flex-end;
	width: 50%;
	margin-bottom: 19px;
	padding-right: 32px;
`;

export const ValueWrapper = styled.div`
	display: inline-flex;
	box-sizing: content-box;
	width: 177px;
	margin-bottom: 19px;
	padding-right: 32px;
`;

export const SelectWrapper = styled.div`
	width: 150px;

	label {
		display: none;
	}

	& span {
		line-height: 24px;
		border-color: ${colors.grey4};
	}
`;

export const ErrorLabel = styled.div`
	color: ${colorError};
	margin-top: 5px;
`;

export const PeriodRange = styled.div`
	margin-left: 8px;
	color: ${grey200};
	font-size: 16px;
	letter-spacing: 0.3px;
`;

export const PeriodWrapper = styled.div`
	position: relative;
`;

export const MonthPickerWrapper = styled.div`
	display: flex;
	z-index: 2;
	margin-top: 3px;
	width: 100%;
	align-items: flex-end;

	.ap-month-picker {
		div {
			box-sizing: content-box;
		}
		> div:first-child {
			border-color: ${grey200};
			width: 250px;
		}
	}

	${({ isActive }) => isActive && `z-index: 3;`};
`;

export const MonthPickerBlock = styled.div`
	display: flex;
	flex-direction: column;
	margin-right: 30px;
`;

export const CurrencyInputWrapper = styled.div`
	margin-right: 20px;
	width: 210px;
`;

export const MonthPickerNameBlock = MonthPickerBlock.extend`
	flex-grow: 1;
`;

export const Label = styled.div`
	text-overflow: ellipsis;
	margin-bottom: 8px;
	color: ${grey500};
	max-width: 100%;
	display: inline-block;
	white-space: nowrap;
	overflow: hidden;
	font-size: 11px;
	font-weight: 400;
	letter-spacing: 0.8px;
	line-height: 13px;
	text-transform: uppercase;
`;

export const SmallInput = styled.div`
	> div {
		display: flex;
		flex-direction: column;
	}

	input {
		font-weight: 400;
		padding: 3px 6px 2px 6px;
		font-size: 16px;
		z-index: 1;

		${({ error }) =>
			error &&
			css`
				border: 1px solid ${colors.red1};
			`};

		&:disabled {
			background-color: inherit;
			&::placeholder {
				color: ${grey200};
			}
		}
	}
`;

export const ActionWrapper = styled.div`
	width: 24px;
	display: flex;
	margin-bottom: 6px;

	svg {
		cursor: pointer;
	}
`;

export const CurrencyRadio = styled(Radio)`
	svg.ap-icon {
		margin: 0;
	}

	> div {
		> svg {
			align-self: flex-end;
			margin-bottom: ${({ isError }) => (isError ? '29px' : '9px')};
		}
	}
`;

export const MonthPickerRadio = styled(Radio)`
	> div {
		padding-bottom: 7px;

		> svg {
			align-self: flex-end;
			margin-bottom: 6px;
		}
	}
`;

export const LinkButton = styled(ApButtonLink)`
	margin-left: 5px;
	margin-top: 8px;

	&:hover {
		color: ${hoverGreen};
	}
`;

export const SectionFormLabel = styled.div`
	flex: 0 0 100%;
	font-size: 10px;
	font-style: normal;
	font-weight: 400;
	line-height: 19px;
	letter-spacing: 0.3px;
	text-align: left;
	color: ${colors.grey5};
	margin-bottom: 4px;
`;

export const SectionForm = styled.div`
	display: flex;
	flex-wrap: wrap;

	${SelectWrapper} {
		label {
			display: none;
		}
	}

	${ValueWrapper} {
		width: 120px;
		padding-right: 16px;
	}
`;

export const InitiativeSubTitle = styled.span`
	display: block;
	width: 100%;
	font-size: 16px;
	font-weight: 500;
	color: ${colors.black};
	margin-bottom: 24px;
`;
export const MultiSelectWrapper = styled.span`
	> div {
		> div {
			border-radius: 0;
		}
	}
`;
export const TooltipContent = styled.div`
	font-size: 11px;
	text-align: left;
`;
export const TableWrapper = styled.div`
	position: relative;
	width: 100%;
	height: ${({ height }) => (height ? `${height}px` : 'auto')};

	.ag-theme-balham {
		width: 100%;
		height: 100%;
		font-family: 'Roboto';

		.ag-root,
		.ag-side-bar,
		.ag-root-wrapper,
		.ag-status-bar {
			border: none;
		}

		.ag-root-wrapper {
			border-bottom: none;
		}

		.ag-header-row {
			border-bottom: 0px;
		}
		.ag-header {
			background-color: transparent;
			border: none;
			font-family: 'Roboto';

			.dimension-header {
				.ag-header-cell-text {
					position: relative;
					padding-left: 20px;

					&:before {
						content: '\\2B24';
						font-size: 10px;
						position: absolute;
						left: 0;
						top: -1px;
					}
				}

				&.color-1 .ag-header-cell-text:before {
					color: ${CHART_COLORS[0]};
				}
				&.color-2 .ag-header-cell-text:before {
					color: ${CHART_COLORS[1]};
				}
				&.color-3 .ag-header-cell-text:before {
					color: ${CHART_COLORS[2]};
				}
				&.color-4 .ag-header-cell-text:before {
					color: ${CHART_COLORS[3]};
				}
				&.color-5 .ag-header-cell-text:before {
					color: ${CHART_COLORS[4]};
				}
				&.color-6 .ag-header-cell-text:before {
					color: ${CHART_COLORS[5]};
				}
				&.color-7 .ag-header-cell-text:before {
					color: ${CHART_COLORS[6]};
				}
				&.color-8 .ag-header-cell-text:before {
					color: ${CHART_COLORS[7]};
				}
				&.color-9 .ag-header-cell-text:before {
					color: ${CHART_COLORS[8]};
				}
				&.color-10 .ag-header-cell-text:before {
					color: ${CHART_COLORS[9]};
				}
			}

			.ag-cell-label-container {
				overflow: hidden;
			}
		}

		.ag-body-viewport.ag-layout-auto-height .ag-center-cols-clipper {
			min-height: 24px;
		}

		.ag-header-cell {
			font-weight: normal;
			padding-bottom: 5px;
			font-size: 13px;
			line-height: 19px;
			letter-spacing: 0.3px;
			color: ${colors.grey5};
		}

		.ag-row {
			background-color: transparent;
			border-color: ${colors.grey3};

			&.ag-row-last {
				border-bottom: none;
			}

			.ag-cell {
				font-size: 13px;
				font-weight: normal;
				line-height: 24px;
				letter-spacing: 0.3px;
				color: ${colors.grey8};
			}
		}

		.ag-floating-top-viewport {
			border: none;
		}

		.ag-floating-top {
			border-color: ${colors.grey3};
		}

		.ag-row-pinned {
			border: none;

			&.ag-row-last {
				border: none;
			}

			.ag-cell {
				font-weight: bold;
			}
		}

		.ag-header-row-column {
			border-bottom: 1px solid ${colors.grey3};
		}
	}
`;
export const ScrollWrapper = styled.div`
	height: 60vh;
	overflow-y: scroll;
`;
